import { Fragment, h, ListView } from "harmaja"
import { getNavigator, Link } from "harmaja-router"
import * as L from "lonna"
import * as uuid from "uuid"
import {
    Board,
    BoardAccessPolicy,
    BoardStub,
    CrdtDisabled,
    CrdtEnabled,
    EventFromServer,
    newBoard,
    RecentBoard,
    ServerConfig,
} from "../../../common/src/domain"
import { BOARD_PATH, createBoardAndNavigate, Routes } from "../board-navigation"
import { isMermaidFlowchart, mermaidToItems } from "../board/mermaid-import"
import { arrayToRecordById } from "../../../common/src/arrays"
import { IS_TOUCHSCREEN } from "../board/touchScreen"
import { BoardAccessPolicyEditor } from "../components/BoardAccessPolicyEditor"
import { BoardCrdtModeSelector } from "../components/BoardCrdtModeSelector"
import { TextInput } from "../components/components"
import { canLogin, defaultAccessPolicy, UserSessionState } from "../store/user-session-store"
import { signIn, signOut } from "../google-auth"
import { Dispatch } from "../store/board-store"
import { RecentBoards } from "../store/recent-boards"
import { ThemeToggle } from "../theme"

type PublicBoard = {
    id: string
    name: string
    updatedAt: string | null
    frameCount: number
    itemCount: number
    boxes: { x: number; y: number; w: number; h: number; color?: string; type: string }[]
}

const VERSION = process.env.LOUSA_VERSION || "dev"

function timeAgo(iso: string | null): string {
    if (!iso) return ""
    const diff = Date.now() - new Date(iso).getTime()
    const min = Math.floor(diff / 60000)
    if (min < 1) return "just now"
    if (min < 60) return `${min} min ago`
    const h = Math.floor(min / 60)
    if (h < 24) return `${h} h ago`
    const d = Math.floor(h / 24)
    return d === 1 ? "yesterday" : `${d} d ago`
}

const Thumbnail = ({ meta }: { meta: PublicBoard | undefined }) => {
    if (!meta || meta.boxes.length === 0) return <div className="thumb empty" />
    const boxes = meta.boxes
    const minX = Math.min(...boxes.map((b) => b.x))
    const minY = Math.min(...boxes.map((b) => b.y))
    const maxX = Math.max(...boxes.map((b) => b.x + b.w))
    const maxY = Math.max(...boxes.map((b) => b.y + b.h))
    const pad = Math.max(maxX - minX, maxY - minY) * 0.08 + 1
    const vb = `${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`
    return (
        <div className="thumb">
            <svg viewBox={vb} preserveAspectRatio="xMidYMid meet">
                {boxes.map((b) => (
                    <rect
                        x={b.x}
                        y={b.y}
                        width={b.w}
                        height={b.h}
                        rx={0.6}
                        fill={b.type === "container" ? "transparent" : b.color || "var(--surface-2)"}
                        stroke={b.type === "container" ? "var(--text-muted)" : "rgba(43,42,51,0.15)"}
                        stroke-width={b.type === "container" ? 0.5 : 0.25}
                    />
                ))}
            </svg>
        </div>
    )
}

export const DashboardView = ({
    sessionState,
    dispatch,
    recentBoards,
    eventsFromServer,
    serverConfig,
}: {
    sessionState: L.Property<UserSessionState>
    recentBoards: RecentBoards
    dispatch: Dispatch
    eventsFromServer: L.EventStream<EventFromServer>
    serverConfig: L.Property<ServerConfig | null>
}) => {
    const navigator = getNavigator<Routes>()
    const boardName = L.atom("")
    const disabled = L.view(boardName, (n) => !n)
    const accessPolicy: L.Atom<BoardAccessPolicy | undefined> = L.atom(defaultAccessPolicy(sessionState.get(), false))
    sessionState.onChange((s) => accessPolicy.set(defaultAccessPolicy(s, false)))
    const useCollaborativeEditingAtom = L.atom(false)
    const useCollaborativeEditing = L.view(serverConfig, useCollaborativeEditingAtom, (c, u) =>
        c?.crdt === "true" ? true : c?.crdt === "false" ? false : u,
    )
    const crdtMode = () => (useCollaborativeEditing.get() ? CrdtEnabled : CrdtDisabled)

    const publicBoards = L.atom<PublicBoard[] | null>(null)
    fetch("/api/v1/boards")
        .then((r) => r.json())
        .then((d) => publicBoards.set(d.boards))
        .catch(() => {})

    function createNamed(e: JSX.FormEvent) {
        e.preventDefault()
        if (disabled.get()) return
        create({ name: boardName.get(), id: uuid.v4(), accessPolicy: accessPolicy.get(), crdt: crdtMode() })
    }
    function create(stub: BoardStub | Board) {
        createBoardAndNavigate(stub, dispatch, navigator, eventsFromServer)
    }
    const createBlank = () =>
        create({ name: "Untitled board", id: uuid.v4(), accessPolicy: accessPolicy.get(), crdt: crdtMode() })
    const createTutorial = () =>
        create({
            id: uuid.v4(),
            name: "My personal tutorial board",
            templateId: "tutorial",
            accessPolicy: accessPolicy.get(),
        } as BoardStub)

    const fileInput = L.atom<HTMLInputElement | null>(null)
    async function onImportFile(e: JSX.InputEvent<HTMLInputElement>) {
        const file = e.currentTarget.files?.[0]
        if (!file) return
        const text = await file.text()
        try {
            if (isMermaidFlowchart(text)) {
                const result = mermaidToItems(text, { x: 100, y: 60 }, CrdtDisabled)
                if (!result) throw Error("Could not parse the mermaid file")
                const b: Board = {
                    ...newBoard(file.name.replace(/\.(mmd|txt|mermaid)$/i, ""), CrdtDisabled, accessPolicy.get()),
                    items: arrayToRecordById(result.items),
                    connections: result.connections,
                }
                create(b)
            } else {
                const parsed = JSON.parse(text)
                const board: Board = parsed.board ?? parsed
                if (!board || typeof board !== "object" || !board.items) throw Error("Not a board JSON export")
                create({ ...board, id: uuid.v4(), serial: 0, accessPolicy: accessPolicy.get() })
            }
        } catch (err: any) {
            alert(`Import failed: ${err?.message ?? err}`)
        } finally {
            e.currentTarget.value = ""
        }
    }

    const metaById = L.view(publicBoards, (bs) => new Map((bs ?? []).map((b) => [b.id, b])))
    const shared = L.view(publicBoards, recentBoards.recentboards, (bs, recent) => {
        const recentIds = new Set(recent.map((r) => r.id))
        return (bs ?? []).filter((b) => !recentIds.has(b.id))
    })
    const boardCount = L.view(publicBoards, recentBoards.recentboards, (bs, recent) => {
        const ids = new Set([...(bs ?? []).map((b) => b.id), ...recent.map((r) => r.id)])
        return ids.size
    })

    return (
        <div id="root" className="dashboard">
            <div className="dash-topbar">
                <span className="brand">
                    <span className="logo-mark">L</span>
                    Lousa
                </span>
                <span className="right">
                    <span className="server-chip">{location.host}</span>
                    <ThemeToggle />
                    {L.view(sessionState, (user) =>
                        user.status === "logged-in" ? (
                            <span className="avatar" title={`${user.name} · Sign out`} onClick={signOut}>
                                {user.name
                                    .split(/\s+/)
                                    .map((w) => w[0])
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase()}
                            </span>
                        ) : canLogin(user) ? (
                            <a className="sign-in" onClick={signIn}>
                                Sign in
                            </a>
                        ) : null,
                    )}
                </span>
            </div>
            <div className="dash-body">
                <div className="dash-sidebar">
                    <h1>Boards</h1>
                    <p className="count">
                        {L.view(boardCount, (n) => (n === 1 ? "1 board on this server" : `${n} boards on this server`))}
                    </p>
                    <form onSubmit={createNamed} className="create-board">
                        <TextInput value={boardName} autoFocus={!IS_TOUCHSCREEN} placeholder="New board name" />
                        <button data-test="create-board-submit" type="submit" disabled={disabled}>
                            Create board
                        </button>
                        {L.view(disabled, sessionState, serverConfig, (d, s, c) => {
                            if (d) return null
                            const optInCollaborative =
                                c?.crdt === "opt-in" || (c?.crdt === "opt-in-authenticated" && s.status === "logged-in")
                            return (
                                <div className="board-options">
                                    {optInCollaborative ? (
                                        <BoardCrdtModeSelector
                                            {...{ useCollaborativeEditing: useCollaborativeEditingAtom }}
                                        />
                                    ) : null}
                                    {s.status === "logged-in" ? (
                                        <BoardAccessPolicyEditor {...{ accessPolicy, user: s }} />
                                    ) : (
                                        <small className="anonymousBoardDisclaimer">
                                            Anonymously created boards are accessible to anyone with a link.
                                        </small>
                                    )}
                                </div>
                            )
                        })}
                    </form>
                    <p className="micro-label">Start from</p>
                    <ul className="start-from">
                        <li onClick={createBlank}>Blank board</li>
                        <li onClick={createTutorial}>Tutorial board</li>
                        <li onClick={() => fileInput.get()?.click()}>
                            Import…
                            <input
                                ref={fileInput.set}
                                type="file"
                                accept=".json,.mmd,.txt,.mermaid"
                                style={{ display: "none" }}
                                onInput={onImportFile}
                            />
                        </li>
                    </ul>
                    <div className="sidebar-footer">
                        Self-hosted · {VERSION}
                        <br />
                        All data stays on your server.
                    </div>
                </div>
                <div className="dash-main">
                    <div className="recent-head">
                        <h2>Recent</h2>
                        <span className="sort-hint">Sort: last opened</span>
                    </div>
                    <div className="board-grid">
                        <ListView
                            observable={recentBoards.recentboards}
                            getKey={(b: RecentBoard) => b.id}
                            renderItem={(b) => (
                                <Link<Routes> route={BOARD_PATH} boardId={b.id} className="board-card">
                                    {L.view(metaById, (m) => (
                                        <Thumbnail meta={m.get(b.id)} />
                                    ))}
                                    <div className="card-body">
                                        <div className="name">{b.name || "Unnamed board"}</div>
                                        {L.view(metaById, (m) => {
                                            const meta = m.get(b.id)
                                            return (
                                                <div className="subline">
                                                    {meta?.updatedAt
                                                        ? `Edited ${timeAgo(meta.updatedAt)}`
                                                        : "Opened earlier"}
                                                    {meta
                                                        ? ` · ${meta.frameCount} ${
                                                              meta.frameCount === 1 ? "frame" : "frames"
                                                          }`
                                                        : ""}
                                                </div>
                                            )
                                        })}
                                    </div>
                                    <span
                                        className="remove"
                                        title="Remove from recent"
                                        onClick={(e) => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            recentBoards.removeRecentBoard(b)
                                        }}
                                    >
                                        ×
                                    </span>
                                </Link>
                            )}
                        />
                        <div className="board-card new" onClick={createBlank}>
                            + New board
                        </div>
                    </div>
                    {L.view(shared, (bs) =>
                        bs.length === 0 ? null : (
                            <div className="shared-row">
                                <span className="label">Shared on this server:</span>
                                {bs.map((b) => (
                                    <Link<Routes> route={BOARD_PATH} boardId={b.id}>
                                        {b.name || "Unnamed board"}
                                    </Link>
                                ))}
                            </div>
                        ),
                    )}
                </div>
            </div>
        </div>
    )
}
